/**
 * Curated podcast-style narration scripts — trackers & operations pages.
 * Keyed by ListenButton guideId. See index.js for the writing style guide.
 */

export const OPERATIONS_NARRATIONS = {
  /**
   * Full new-employee video tour — paired with PipingProgressWalkthrough.js.
   * Section order/titles must stay aligned with PIPING_WALKTHROUGH_SCENES.
   */
  'piping-progress-walkthrough': [
    {
      title: 'Welcome to the piping tracker',
      icon: '🎬',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome aboard. If you are new to Laframboise, this tour is your map of the piping progress tracker — the place where shop fabrication and field installation actually get recorded.',
        },
        {
          speaker: 'liam',
          speech: 'And here is why it matters on day one: every honest update you type here becomes credit, earned hours, earned credit, and total field completion. Those numbers feed the earned report, the budget report, the earned value report, the schedule of values report, and — when Activity I D is filled in — the schedule itself.',
        },
        {
          speaker: 'clara',
          speech: 'So this is not a side spreadsheet. It is the heartbeat of project reporting. Stick with us and we will walk every major piece, with pictures on screen as we talk.',
        },
      ],
    },
    {
      title: 'Finding Piping in the portal',
      icon: '🧭',
      turns: [
        {
          speaker: 'liam',
          speech: 'First, pick your project from the header dropdown. Search by name or job number — you will use that muscle every day.',
        },
        {
          speaker: 'clara',
          speech: 'Then open Progress Tracking in the left sidebar and choose Piping. That lands you on the progress tracker matrix. From the same Piping family you can also jump to the N D E tracker and the Testing tracker — we will cover those near the end.',
        },
      ],
    },
    {
      title: 'The matrix at a glance',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'Think of one row as one isometric or line item. On the left you get identity — I S O sheet, fluid code, area, system, sizes and lengths. In the middle sit the progress steps you actually edit. On the right, the portal shows calculated values like credit, earned hours, and total field completion.',
        },
        {
          speaker: 'liam',
          speech: 'Source columns usually arrive from Excel import or from an awarded estimate upload. Progress steps are your job. Calculated columns are read-only on purpose — the database owns those formulas so everyone sees the same truth.',
        },
      ],
    },
    {
      title: 'Freeze Rules of Credit first',
      icon: '🔒',
      turns: [
        {
          speaker: 'liam',
          speech: 'Before anyone can enter progress, Rules of Credit must be frozen for piping on that job. Until then you will see a lock banner.',
        },
        {
          speaker: 'clara',
          speech: 'A project manager or technical coordinator opens Rules of Credit, checks that the field-step weights add up to one hundred percent, and clicks freeze for project. After that, weights stay locked for the life of the job so historical earned values keep meaning something. Only a superadmin can unfreeze to fix a mistake.',
        },
      ],
    },
    {
      title: 'Shop fabrication stages',
      icon: '🏭',
      turns: [
        {
          speaker: 'clara',
          speech: 'Total Shop Completion is not a free-typed percent. It moves through named shop stages: zero means not started, thirty five is boxed and cut, forty is issued to bench, sixty five is tacked, ninety five is welded and ready to ship, and one hundred means loaded — shipped out of the shop.',
        },
        {
          speaker: 'liam',
          speech: 'Pick the stage that matches reality on the floor. Shop earned hours come from that stage times budget hours times the shop share of the shop-field ratio. If the shop is Field Run, that pipe has no fabrication — Total Shop Completion shows N A, fab credit is ignored, and the line is left out of overall fabrication progress. Rare case: if it really is fabricated on site, put Site in the shop column so it still counts as fabrication.',
        },
      ],
    },
    {
      title: 'Field steps and weighted completion',
      icon: '📐',
      turns: [
        {
          speaker: 'liam',
          speech: 'Field work is a set of steps — transport to the install area, erect supports and hangers, fit-up, bolt-up or weld, and line cleanup or hydro. Each step is scored at zero, twenty five, fifty, seventy five, or one hundred percent.',
        },
        {
          speaker: 'clara',
          speech: 'Rules of Credit decide how much each step is worth. Defaults are often fifteen, twenty, ten, thirty five, and twenty — but your frozen project weights win. Total Field Completion is simply those step percents multiplied by their weights. Field earned hours then use budget hours, the field share of the shop-field ratio, and that weighted completion.',
        },
      ],
    },
    {
      title: 'Working like a spreadsheet',
      icon: '⚡',
      turns: [
        {
          speaker: 'clara',
          speech: 'The grid is meant to feel like Excel. Click a cell, type or pick a value, tab moves right, enter saves and drops down. There is no separate save button — leaving a cell is the save.',
        },
        {
          speaker: 'liam',
          speech: 'Need fifty rows at the same percent? Grab the little fill handle and drag. Every column header has a filter so you can narrow to one area or one system in seconds. You can hide, rename, and reorder columns, and your layout is remembered per project. Teammates editing at the same time show up live with presence indicators.',
        },
      ],
    },
    {
      title: 'Getting data in and out',
      icon: '📦',
      turns: [
        {
          speaker: 'liam',
          speech: 'Import brings source columns from Excel — identification, sizing, budget information, roughly the columns the tracker should not invent. Progress steps, calculated values, and system fields are intentionally left out of that template so an import can never wipe field progress.',
        },
        {
          speaker: 'clara',
          speech: 'Export goes the other way any time. Choose just the columns on screen, all columns, or a full export including hidden fields. Big imports run as background jobs, so you can keep working while the file churns.',
        },
      ],
    },
    {
      title: 'Line history and trends',
      icon: '📈',
      turns: [
        {
          speaker: 'clara',
          speech: 'See that blue line tag in the first column? Click it. A progress trends chart opens for that I S O, built from the portal snapshots that quietly run behind the scenes.',
        },
        {
          speaker: 'liam',
          speech: 'That history is gold in a status meeting. Even if someone edits a value later, the weekly story of how the line moved is still there for you.',
        },
      ],
    },
    {
      title: 'Holds, comments, and teammates',
      icon: '💬',
      turns: [
        {
          speaker: 'liam',
          speech: 'The hold checkbox flags a line as on hold — visually marked and excluded from certain report calculations when scope is in limbo. Do not clear a hold unless you know why it was set.',
        },
        {
          speaker: 'clara',
          speech: 'Comments accept at-mentions, so you can pull a teammate into a conversation on that exact row. And those little avatars in the corner? That is live presence — who else is in the tracker right now, so you do not step on each other.',
        },
      ],
    },
    {
      title: 'Link lines to the schedule',
      icon: '🔗',
      turns: [
        {
          speaker: 'clara',
          speech: 'Activity I D is the Primavera activity code from the schedule — the same code you see on the Gantt. Put it on each tracker line and the portal rolls those lines up by hours into that activity percent complete on Schedule Instances, live, all week long.',
        },
        {
          speaker: 'liam',
          speech: 'Who fills it in? The scheduler. Many isometrics can share one activity — that is normal. Leave it blank and the line still earns into reports, but it will not move the schedule. Type it in the grid or bring it with Excel import.',
        },
      ],
    },
    {
      title: 'Analytics and downstream reports',
      icon: '📉',
      turns: [
        {
          speaker: 'liam',
          speech: 'Open Analytics when you want the project-level story — completion, earned hours, breakdowns by area, system, or S O V code. On the S O V tab, switch to Client for credits only, or Internal for hours and credits together. It is the same live data you just edited, rolled up.',
        },
        {
          speaker: 'clara',
          speech: 'Then remember the Reports menu: earned, budget, earned value, and schedule of values all drink from this well. Accurate tracker entry is how those reports stay trustworthy for the client and for internal control.',
        },
      ],
    },
    {
      title: 'NDE and Testing trackers',
      icon: '🧪',
      turns: [
        {
          speaker: 'clara',
          speech: 'Under the same Piping dropdown sit two sister tools. The N D E tracker answers: is this weld sound? It records examinations like X-Ray, liquid penetrant, magnetic particle, ultrasonic, visual, and positive material identification, with pass and fail quantities per weld.',
        },
        {
          speaker: 'liam',
          speech: 'The Testing tracker asks a different question: once a whole system is welded, does it hold pressure the way the design says? That is A S M E B thirty one point three or B thirty one point one testing — hydrostatic, pneumatic, or leak tests — tracked by line or system and fluid code, not by individual weld.',
        },
        {
          speaker: 'clara',
          speech: 'You can seed Testing rows from fluid codes already sitting in the progress tracker, and both sisters keep audit logs so you always know who changed what.',
        },
        {
          speaker: 'liam',
          speech: 'Open Analytics on either tracker when you need a client-ready summary. The Report page is the picture Quality asked for: welders and joints on N D E, or system completion on Testing.',
        },
        {
          speaker: 'clara',
          speech: 'Excel is still there for the raw numbers. P D F prints that same Report layout with the company letterhead. Email attaches that P D F so you can send a summary without reformatting.',
        },
      ],
    },
    {
      title: 'When an ISO is re-issued',
      icon: '🔁',
      turns: [
        {
          speaker: 'liam',
          speech: 'After an estimate is awarded, a new M T O revision can re-issue an isometric at a new version. The old tracker line stays in the grid, struck through and read-only, with its progress preserved. A new active line is appended with the new quantities and tagged Extra so baseline versus change stays clear.',
        },
        {
          speaker: 'clara',
          speech: 'If the old line already had progress, the new active line is put on hold automatically. Change orders capture the scope delta and claim the earned hours from the superseded line, because live earned reports skip those struck-through rows. Never edit a superseded line — it is history on purpose.',
        },
      ],
    },
    {
      title: 'Your first week on the job',
      icon: '✅',
      turns: [
        {
          speaker: 'clara',
          speech: 'Day-one checklist: confirm Rules of Credit are frozen, filter the matrix to your area, update shop and field steps to match the floor, use import for source data only, click a line tag when you need the trend story, and ask before clearing a hold or touching anything struck through.',
        },
        {
          speaker: 'liam',
          speech: 'That is the whole piping progress family. Accurate, calm updates here make every report downstream look smart. Welcome to the team — go make the matrix tell the truth.',
        },
      ],
    },
  ],

  'discipline-matrix-help': [
    {
      title: 'Why this tracker matters',
      icon: '🎯',
      speech: 'Picture a construction project with hundreds or thousands of line items, each one somewhere between not started and done. How do you know where the project really stands? That is what this progress tracker answers. It is the central place where field progress gets recorded, for both shop fabrication and field installation, and it automatically turns those updates into the numbers management lives by: credit, earned hours, earned credit, and total field completion. Here is the part worth remembering: every single edit you make here flows straight into the earned report, the budget report, the earned value report, the schedule of values report, and — when the Activity I D is filled in — the project schedule percent complete, in real time. Accurate entry here is not busywork, it is the heartbeat of project reporting.'
    },
    {
      title: 'Link lines to the schedule',
      icon: '🔗',
      turns: [
        {
          speaker: 'clara',
          speech: 'Two schedule columns do more than they look. Activity I D is the Primavera install activity — Pipe Install or Mechanical Install. Put it on each tracker line, and the portal rolls field completion into that activity on Schedule Instances — live, as people edit, all week long.'
        },
        {
          speaker: 'liam',
          speech: 'On piping there is a second pair: Fab Activity I D and Fab Activity Name. Those point at Pipe Fab. Shop completion rolls there, and Field Run lines are left out because they have no fabrication. Leave both blank and the line still earns into reports, but it will not move the schedule. The scheduler owns the mapping; type it in or import it. Wieland can auto fill both from the fluid code.'
        }
      ]
    },
    {
      title: 'How progress is measured',
      icon: '📐',
      speech: 'Each line item moves through a set of progress steps, and each step is scored at zero, twenty five, fifty, seventy five, or one hundred percent complete. But not all steps are worth the same. The system uses something called rules of credit, which are per project weights that say how much each step contributes to the total. So welding might carry more weight than transporting material to the install area. The tracker multiplies your step percentages by those weights and rolls them up into total field completion automatically. From there, earned hours are simply budget hours times how complete you are, and earned credit works the same way with the credit value. On piping, Field Run lines have no shop fabrication, so they show N A for shop completion and are left out of overall fab percent. If a pipe is fabricated on site, set the shop to Site so it still counts.'
    },
    {
      title: 'Freeze before you track',
      icon: '🔒',
      speech: 'Now here is something that catches people off guard. You cannot enter progress for a discipline until its rules of credit have been frozen. A project manager or technical coordinator opens the rules of credit window, reviews the weights, confirms they add up to one hundred percent, and clicks freeze for project. Once frozen, those weights are locked for the life of the project, and only a superadmin can unfreeze them to make a correction. That sounds strict, but it protects you: if the weights changed mid project, all your historical earned values would stop meaning anything.'
    },
    {
      title: 'Working like a spreadsheet',
      icon: '⚡',
      speech: 'The grid itself behaves a lot like Excel, on purpose. Click a cell to edit it in place. Need to set fifty rows to the same value? Click one cell, grab the little blue handle, and drag it down to fill the whole range. Every column header has a filter, so you can narrow the view down to one area or one system in a couple of clicks. You can also hide, rename, and reorder columns, and your personal layout is remembered per project. And because multiple people can edit at the same time, changes from your teammates show up live, with presence indicators telling you who else is in the tracker right now.'
    },
    {
      title: 'Getting data in and out',
      icon: '📦',
      speech: 'Source data comes in through an Excel import. The template covers the source columns, things like line identification, sizing, and budget information, roughly forty columns worth. Notice what is not in the template: progress steps, calculated values, and system fields. Those are managed by the tracker itself, so an import can never accidentally overwrite your field progress. Going the other way, you can export to Excel at any time, either just the columns you see on screen or absolutely everything including hidden fields.'
    },
    {
      title: 'Holds, comments, and history',
      icon: '🕰️',
      speech: 'A few smaller tools do a lot of quiet work here. The hold checkbox flags a line item as on hold, which marks it visually and excludes it from certain report calculations, handy when scope is in limbo. Comments accept at mentions, so you can pull a teammate directly into a conversation about a specific line. And behind the scenes, the portal takes periodic snapshots of the whole tracker, which is what powers the time based reports and lets you see how progress evolved over weeks and months, even if someone edits a value later.'
    },
    {
      title: 'When an ISO is re-issued',
      icon: '🔁',
      turns: [
        {
          speaker: 'clara',
          speech: 'On piping, after the estimate is awarded, a new M T O revision can re-issue an isometric at a new version. The old tracker line stays in the grid struck through and read-only, with its progress preserved, and a new active line is appended with the new quantities — tagged as Extra so baseline versus change is clear.'
        },
        {
          speaker: 'liam',
          speech: 'If that old line already had progress, the new active line is put on hold automatically. Two change orders are created in Change Orders Tracking: one for the scope and quantity differences on the takeoff, and a second one that claims the earned hours from the struck-through line. Live earned reports skip superseded rows, so that second change order is how those hours stay attributed.'
        }
      ]
    }
  ],

  'pipe-testing-tracker-help': [
    {
      title: 'A different question than NDE',
      icon: '💧',
      speech: 'The piping tracker family already answers one big question: is this weld sound? That is what the N D E tracker is for, radiography and other examinations on individual welds. The testing tracker asks a completely different question: once a whole system is welded together, does it actually hold pressure the way the design says it should? That is A S M E B thirty one point three or B thirty one point one pressure testing, and instead of tracking one weld at a time, it tracks whole systems, identified by their line or system I D and their fluid code.'
    },
    {
      title: 'What a row represents',
      icon: '📋',
      speech: 'Every row here is one system waiting for, going through, or having finished its pressure test. It carries the design pressure and design temperature the system was built to, the actual test pressure required, which sometimes is not a fixed number at all but a note like operating pressure, the type of test, the medium used to perform it, and how long that pressure has to be held before the test counts. Fill those fields in once, and the row becomes a living record of exactly how that system was proven safe.'
    },
    {
      title: 'Five ways to test a system',
      icon: '🧪',
      speech: 'The type of testing field is where the real engineering judgment shows up. Hydrostatic testing, filling the system with water and pressurizing it, is the default and the gold standard for most services. Pneumatic testing, using dry nitrogen or clean compressed air instead of water, comes in when a system cannot tolerate moisture, but it demands extra caution because compressed gas stores a lot more energy than water does. Service leak testing checks a system at its normal operating pressure using the real service fluid. Sensitive leak testing, often using helium, is reserved for hazardous or environmentally critical services where even a tiny leak is unacceptable. And alternative leak testing covers any other method the code and the project specification agree to. Choosing the right one is a judgment call made with the piping specification in hand, not a guess.'
    },
    {
      title: 'The status workflow',
      icon: '🚦',
      speech: 'Every system starts out pending, before any testing has begun. Once someone is actively pressuring it up and watching the gauge, you move it to in progress. If it holds for the full required time with no drop in pressure, it becomes pass or complete, and that is the moment to fill in the test date, the inspector who witnessed it, and the sign off. If it does not hold, it is marked failed, and here is a handy trick: you can duplicate that failed row, which copies over all the design and specification fields, and use the copy to track the repair and retest as a fresh pending record, without losing the history of what went wrong the first time.'
    },
    {
      title: 'Getting rows in fast',
      icon: '⚡',
      speech: 'You rarely start this tracker from a blank sheet. The import feature can seed new pending rows straight from the distinct fluid codes already sitting in the piping progress tracker for the same job, so the systems you need to test are populated automatically with their fluid code and line or system I D already filled in. From there you just add the design and test details. You can also add rows manually one at a time, or bring in a whole log at once from an Excel file if the data already exists somewhere else.'
    },
    {
      title: 'Seeing the whole picture',
      icon: '📊',
      speech: 'The analytics view turns all those individual rows into a project level story: overall completion percentage, how many systems are pending versus in progress versus passed versus failed, and breakdowns by test type and by fluid code, so you can spot, for example, that every failure so far has been on the same fluid service. Excel is still there for the raw numbers. P D F prints the same Report layout with the company letterhead, and Email attaches that P D F so you can send a client summary without reformatting. Every edit is captured in the audit log, so if a number ever looks surprising, you can always see who changed it and when. That is the testing tracker: one row per system, one clear status, and a growing record of proof that the piping you built actually holds.'
    }
  ],

  'requisition-log-help': [
    {
      title: 'Stop the Word email',
      icon: '📋',
      turns: [
        {
          speaker: 'clara',
          speech: 'A requisition that lives in a Word file on someone\'s laptop is a requisition nobody can find next week. This page is the official material and equipment request form, plus the job log that used to sit in a SharePoint spreadsheet.'
        },
        {
          speaker: 'liam',
          speech: 'The point is simple. One numbered form. One status. One history. No more Pre G T skid packs floating around in Outlook with three different layouts.'
        }
      ]
    },
    {
      title: 'The official form',
      icon: '📝',
      turns: [
        {
          speaker: 'clara',
          speech: 'The header matches the old request sheet: client, contract, requested by, issued by, work area, needed by, and transmittal. Line items still carry size, ident code, tag, quantity, unit, and what is already on site.'
        },
        {
          speaker: 'liam',
          speech: 'Each job numbers itself. First one is R E Q dash zero zero one, then two, then three. If a client already gave you their number, you can type that in instead.'
        }
      ]
    },
    {
      title: 'Who can touch it',
      icon: '👥',
      turns: [
        {
          speaker: 'clara',
          speech: 'Everyone on the project team can open the log, search it, and take a copy. That is the whole point of putting it in the portal instead of a private inbox.'
        },
        {
          speaker: 'liam',
          speech: 'Edits stay with the technical team. Technical coordinators create, change, import, and delete. Superadmins can too. Everyone else is looking, not rewriting.'
        }
      ]
    },
    {
      title: 'Share without reinventing',
      icon: '📤',
      turns: [
        {
          speaker: 'clara',
          speech: 'Excel downloads the log and the line items together, and you can import that same file back. P D F prints the letterhead form or the whole log. Email attaches that P D F so the client gets the official sheet, not a homemade Word doc.'
        },
        {
          speaker: 'liam',
          speech: 'Status follows the life of the request: draft, submitted, issued, requested, received, installed, or void. If a needed by date slips and the item is still open, the row calls it out.'
        }
      ]
    },
    {
      title: 'The paper trail stays',
      icon: '🧾',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every create, edit, delete, import, export, and email lands in the audit log. If a quantity looks surprising, you can see who changed it and when.'
        },
        {
          speaker: 'liam',
          speech: 'Use this page the next time someone asks for a skid or a valve list. Number it here, send the P D F, and leave the untraceable attachments behind.'
        }
      ]
    }
  ],

  'rfi-log-help': [
    {
      title: 'What is the RFI Log?',
      icon: '📨',
      speech: 'When a drawing does not match the field, someone has to ask the question, and that question needs a paper trail. That is the R F I log. A request for information is the formal way a construction team asks the engineer, architect, or owner to clarify design intent, resolve an ambiguity, or fill in missing information. This page is where every one of those questions lives: numbered, tracked, and time stamped, so nothing gets lost in an email thread and everyone can see exactly what is still waiting on an answer.'
    },
    {
      title: 'The life of an RFI',
      icon: '🔁',
      speech: 'Every R F I follows the same story arc. It starts as a draft while you are preparing it, becomes open once it is submitted and waiting for a response, moves to in review while the answer is being evaluated, and finally lands at closed when it is answered and accepted, or void if it gets cancelled. To create one, you give it a title, write the specific question you need answered, and, if you can, include a suggested answer. That last part is a real pro move: when you hand the engineer a proposed solution to approve, responses come back dramatically faster. The system numbers each R F I sequentially per project, so you never have to think about numbering.'
    },
    {
      title: 'Ball in court',
      icon: '🎾',
      speech: 'Here is the concept that makes the whole log click: ball in court. At any moment, exactly one party needs to act on each R F I, and the ball in court field names that person. When you submit a question, the ball goes to whoever owes you the answer. When they respond, it comes back to you. This simple idea turns a vague pile of open questions into a clear accountability list, and there is a ball in court summary view that shows you, at a glance, who is sitting on the most open items.'
    },
    {
      title: 'The clock is always running',
      icon: '⏱️',
      speech: 'One thing people really appreciate here is the automatic time tracking. Every project has a contractual response duration, set in the settings, defaulting to three business days. When you create an R F I, the due date is calculated automatically from that duration, skipping weekends and even statutory holidays for your province or state. If an R F I sails past its due date without being closed, the days to respond turns red with an exclamation mark, so overdue items practically wave at you. The analytics then compare actual response times against the contractual target, which is exactly the evidence you want in a tough conversation about delays.'
    },
    {
      title: 'Finding and sharing',
      icon: '🔍',
      speech: 'As the log grows, the search bar and column filters keep it manageable. You can filter down to just open items, or just high priority ones, and active filters show as little green badges so you always know what you are looking at. When it is time to share, export lets you take the filtered table as you see it, or the complete log, as Excel for clients and stakeholders. And it works the other way too: you can import an existing R F I log from Excel, and the importer is smart enough to recognize several common column naming styles from different templates. If your client runs their own R F I system, the external I D field keeps the two numbering schemes linked.'
    },
    {
      title: 'A few habits that pay off',
      icon: '💡',
      speech: 'Before you go, a few habits that separate a clean R F I log from a chaotic one. Submit questions early, because a late R F I is a schedule risk in disguise. Be specific and reference the drawing or spec right in the question. Categorize by discipline and category so the analytics actually mean something. And make a weekly ritual of scanning for overdue items. Every action here is captured in the audit log, so the history takes care of itself. That is the R F I log. Ask good questions, and keep that ball moving.'
    }
  ],

  'sheets-viewer-help': [
    {
      title: 'Your drawings, marked up together',
      icon: '📐',
      speech: 'Imagine standing around a paper drawing with a red pen, except the whole project team is holding the same pen at once. That is the sheets viewer. It is a P D F viewer for technical drawings, built in the style of Autodesk Build, where anyone on the project can draw, highlight, and comment directly on the sheet. Every markup is saved automatically within seconds and shows up for everyone else in near real time, so the drawing on your screen is always the live, shared copy, never a stale printout.'
    },
    {
      title: 'The drawing toolkit',
      icon: '✏️',
      speech: 'The tools will feel familiar within a minute. There is a pen for freehand sketching, rectangles and ellipses for boxing in areas of interest, arrows for pointing at exactly the right fitting, and a text tool for dropping a note anywhere on the sheet. Shapes can have a border colour, a fill colour, and an opacity setting, and the semi transparent fills are the trick worth knowing: a light yellow wash lets you highlight a whole region while the drawing text underneath stays perfectly readable. There is also a hand tool for panning around once you have zoomed in.'
    },
    {
      title: 'Nothing is set in stone',
      icon: '🔧',
      speech: 'Here is the part that makes it feel like a real editor rather than a stamp machine: every markup stays editable. Switch to the select tool, click any markup, and a little menu appears letting you edit it, duplicate it, or delete it. Drag it to move it, or grab the blue corner handles to resize. While something is selected, extra dropdowns appear in the toolbar so you can change its colour, line thickness, or opacity on the spot, and the change saves immediately. One thing people miss: any user with project access can modify or delete any markup, not just their own, so the sheet stays a living team document.'
    },
    {
      title: 'Getting around the sheet',
      icon: '🔎',
      speech: 'For navigation, hold control and roll the mouse wheel to zoom the P D F without zooming your whole browser. There are also zoom buttons, a dropdown for jumping to a specific level between fifty and three hundred percent, and a fit to view button that snaps you back to seeing the whole sheet. If the drawing came in sideways, the rotate button turns it ninety degrees per click. Multi page documents get page arrows in the toolbar. And your safety net is always there: control z undoes your last action, whether that was a drawing, a delete, a zoom, or a rotation, with up to fifty steps remembered.'
    },
    {
      title: 'Trust, but verify',
      icon: '🧾',
      speech: 'Since everyone shares one canvas, accountability matters. The audit log keeps a time stamped record of who created, changed, or deleted every markup on the sheet. You can also see who else is viewing the sheet right now in the top corner, which is surprisingly useful when you are walking someone through a detail over the phone. A revision comparison feature, which will highlight what changed between two versions of a sheet, is on its way. That is the viewer in a nutshell: zoom in, mark it up, and let auto save do the worrying for you.'
    }
  ],

  'schedule-gantt-help': [
    {
      title: 'The schedule, alive',
      icon: '📅',
      turns: [
        {
          speaker: 'clara',
          speech: 'A schedule that lives in a file on someone\'s laptop is a schedule nobody trusts. This page puts the project schedule where the whole team can see it: a Gantt chart with the activity table on the left and the timeline bars on the right, grouped by work breakdown structure.'
        },
        {
          speaker: 'liam',
          speech: 'You can zoom the timescale through ten levels, from a whole year view down to maximum detail, using the plus and minus buttons or by dragging the timescale sideways, just like in Primavera. It reads like a schedule, but it behaves like a shared workspace.'
        }
      ]
    },
    {
      title: 'Work breakdown, indented',
      icon: '📂',
      turns: [
        {
          speaker: 'clara',
          speech: 'The missing piece from Primavera was the work breakdown tree. Each W B S node is now a parent row, and its activities sit indented underneath, the way a scheduler already thinks about the job.'
        },
        {
          speaker: 'liam',
          speech: 'Click the little arrow to fold a branch closed so you can scan by area instead of scrolling past every weld. Each parent row is a solid band of colour straight across the table, and that same colour carries on as a slim vertical rail down the left edge of everything inside it, so you can always see which branch you are in. The chart itself stays plain, so the colour is telling you about structure and never about dates. Expand all and Collapse all live in the toolbar. Live Primavera is not required, even if that connection is down.'
        }
      ]
    },
    {
      title: 'The weekly loop — when % moves',
      icon: '🔁',
      turns: [
        {
          speaker: 'clara',
          speech: 'Here is the rhythm people miss if they only look at the two clock times. Monday night at nine Eastern, the portal pulls the latest submitted Primavera project into Schedule Instances — dates, structure, resources. That is step one.'
        },
        {
          speaker: 'liam',
          speech: 'Then comes the live window: from that Monday nine P M pull, all the way until the next Monday six A M export, tracker edits roll into the schedule. Field percent lands on the install Activity I D. On piping, shop percent lands on the Fab Activity I D. Not a batch job later — it updates as people save progress during the week.'
        },
        {
          speaker: 'clara',
          speech: 'Monday morning at six Eastern, one email goes to Yousef and Behzad with every job in it. The full schedule workbooks are attached, plus a Portal import sheet: the Activity I D, and a short note of what changed since last week. That same note is written into the Primavera field called Portal import, on each job’s Current project.'
        }
      ]
    },
    {
      title: 'Activity I D is the scheduler\'s job',
      icon: '🔗',
      turns: [
        {
          speaker: 'liam',
          speech: 'None of that live feed works without Activity I D on the tracker lines, and on piping without Fab Activity I D for shop work. Those columns are the Primavera activity codes — the same ones you see on the Gantt.'
        },
        {
          speaker: 'clara',
          speech: 'For new jobs, the scheduler puts those I Ds on the trackers — type them in or import them. Wieland can auto fill install and fab from the fluid code. After that, it is a human mapping job, on purpose.'
        }
      ]
    },
    {
      title: 'Make it tell your story',
      icon: '🎨',
      turns: [
        {
          speaker: 'liam',
          speech: 'You can still shape the view. The default colours each work breakdown level. Open More to pick a level colour, or to colour bars by discipline, phase, project, or critical path. Labels sit inside the bar or to the right. Row height is there too: small, medium, or large. Medium is the height you start with, and this browser remembers it. Activity layout is the classic columns. Resource assignment layout is the resource detail. Same data underneath.'
        },
        {
          speaker: 'clara',
          speech: 'Search, funnels, and auto fit keep a big schedule navigable. Cream columns let you override percent or remaining duration if you must, but prefer the tracker feed. Notes on activities and on the schedule itself both ride along in the Monday morning export. Changes keeps the old value and the new value, with who saved it, including when a tracker overwrites the percent. The search box in Changes finds an activity, a person, or a value.'
        }
      ]
    },
    {
      title: 'Your work, the next three weeks',
      icon: '👤',
      turns: [
        {
          speaker: 'liam',
          speech: 'When Primavera has an update responsibility on the activity, that code is the employee who should status it. If your portal login is linked to that same employee code, the schedule opens on your activities. All activities is one click away, and this browser remembers which you picked.'
        },
        {
          speaker: 'clara',
          speech: 'Three week look ahead keeps what overlaps the Primavera data date plus twenty one days, and it keeps late remaining work even when the bar is already behind. Finished activities stay out. If the job has no data date stored yet, the window starts today.'
        }
      ]
    },
    {
      title: 'The Friday ask',
      icon: '✉️',
      turns: [
        {
          speaker: 'liam',
          speech: 'When the scheduler turns on the Friday request under Update, the portal sends each responsible person one e-mail every Friday at seven in the morning, Eastern, on behalf of Project Controls. Who is on that list is the scheduler\'s call: the Upd Resp field from P six, or a list typed right in the Update window, one row per person with the slice of the schedule they own, a W B S node, a trade, or the whole thing.'
        },
        {
          speaker: 'clara',
          speech: 'Two P D Fs ride along, both cut to that slice and laid out as the W B S tree, the way P six shows it. The three week look ahead starts at the Sunday midnight cut off, the new data date, with boxes to write in. And the full schedule, for anyone who wants the big picture. The subject line already says how many activities are coming up, so nobody has to open it to know whether it matters this week.'
        },
        {
          speaker: 'liam',
          speech: 'And the ask is short and firm. Physical percent complete as of the cut off, remaining duration if they have it, any obstacle, and any date or sequence that is off, back before Sunday midnight. Reply with the marked up sheet or type it in the e-mail. The project manager and Project Controls are in copy, and anything after the cut off rolls into the following week.'
        }
      ]
    }
  ],

  'change-log-help': [
    {
      title: 'Where changes become money',
      icon: '💰',
      speech: 'On every project, the scope you bid is never quite the scope you build. The change log is where that gap gets managed. It is the central record of every change order and extra on the project, tracking each one from the first rough estimate all the way through submission, client approval, invoicing, and completion. If you have ever lost money on an extra because the paperwork trailed the work, this page exists precisely so that never happens again.'
    },
    {
      title: 'Four flavours of change',
      icon: '🏷️',
      speech: 'Every entry starts with a choice of change type, and there are four. Lump sum is a fixed price change, built internally from estimated costs plus markup, where the client only ever sees the selling totals. T and M, time and materials, is cost plus: labour by trade with straight time, overtime, and double time rates, plus material, subcontractors, and equipment with markup, and here the full detail does print on the client document. Tracking only is for record keeping, no cost details required. And material supply only covers changes with no labour at all. Under the hood, all four share the same internal cost builder, the difference is simply how much of that buildup the client gets to see.'
    },
    {
      title: 'Status runs on autopilot',
      icon: '🚦',
      speech: 'Now here is the clever part: you never pick the status from a dropdown. It is derived automatically from the money boxes. Put a value in estimated dollars and the entry is pending. Once the cost line items generate a submitted dollar amount, it advances to submitted. Enter an approved price and it becomes approved. Tracking only entries show as tracking. The only manual states are cancelled, from the cancel and restore button in the toolbar, and rejected, from the button beside the approved price. The rows colour themselves to match, so a quick glance down the table tells you exactly where every extra stands, white for pending, shades of green as things get submitted and approved, and strikethrough grey for the dead ones.'
    },
    {
      title: 'From estimate to extra number',
      icon: '🔁',
      speech: 'The workflow reads like a story. You pick the change type and discipline, then enter a rough estimated hours and dollars, what estimators affectionately call a swag. You build up the real cost detail, attach drawings, R F I references, and schedule impact. When you submit, the extra number is generated automatically and Accounting gets an email with the description, the estimate, and a link to the portal, no one has to remember to tell them. From there you track submitted versus approved pricing, generate a P D F for the client, and record invoice information once it settles. There is even a separate Maestro column for tracking whether the extra has been opened or approved in the accounting system, independent of the lifecycle status.'
    },
    {
      title: 'Accounting stays in the loop',
      icon: '📬',
      turns: [
        {
          speaker: 'clara',
          speech: 'Two emails keep Accounting current, and both are driven from this Change Log, not the old hidden change orders list.'
        },
        {
          speaker: 'liam',
          speech: 'The first is instant. The moment you add an entry, Accounting gets the extra number, the estimate, and a portal link so they can open it in Maestro.'
        },
        {
          speaker: 'clara',
          speech: 'The second lands every Monday morning. One email per job, a full snapshot of pending, submitted, and approved extras, plus Maestro status. It is everything on the log today, not only what moved last week.'
        },
        {
          speaker: 'liam',
          speech: 'If you do not need those emails, turn them off under Profile, Notification Settings. The list is also visible to admins under Notifications.'
        },
        {
          speaker: 'clara',
          speech: 'And cost clerks can open this page themselves. Once the extra is open in Maestro, they set the Maestro column right here. The rest of Accounting just gets the emails.'
        }
      ]
    },
    {
      title: 'Trades and rates, configured once',
      icon: '⚙️',
      speech: 'One thing worth setting up early is the configuration button at the top of the page. That is where you define the project trades, and each trade carries its own work calendar, its own straight time, overtime, and double time rates, and its own room and board and transport per diems. Then, in the add and edit form, picking a trade from the dropdown auto fills all of it, and the trade calendar automatically splits total hours into straight time and overtime for you. You can still override anything, but configure it once and every change order after that gets faster and more consistent. Keep in mind that room and board, transport, and self assessed tax land in the change order total without markup.'
    },
    {
      title: 'Keeping it under control',
      icon: '🧭',
      speech: 'A few closing tips. Single-click a simple cell — notes, invoice number, dates, approved price — to type right there. Double-click the row when you need the full form, the cost builder, drawings, and R F I list. Use the view dropdown in the toolbar to focus the table on a single discipline when things get busy. Open Columns to hide what you do not need, pin Extra or Description so they stay put while you scroll the money columns, or drag the handle and use the arrows to put them where you want — that layout stays on your browser. Do not delete extras that fall through, cancel them instead, so they stay visible with their strikethrough and the record stays complete. And remember the status precedence: if an entry qualifies for several states at once, the most advanced one wins, so an entry with both an estimate and an approved price shows as approved. That is the change log. Capture every change early, and the money follows the paperwork instead of the other way around.'
    }
  ],

  'indirect-resources-analytics-help': [
    {
      title: 'Four views, one window',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'Analytics is the meeting room for this plan. Histogram, pivot, over allocation, and under allocation all live in the same window, so switching tabs does not resize the box. If a table is taller than the pane, you just scroll.'
        },
        {
          speaker: 'liam',
          speech: 'Histogram is the stacked picture. Pivot groups people by job, discipline, role, employee, or shop — click plus to open the week cells. Those headers stay put while you scroll. Over allocation and under allocation both start grouped by person and collapsed. Untick Group by employee if you want every week as its own row.'
        }
      ]
    },
    {
      title: 'Colours and codes',
      icon: '🎨',
      turns: [
        {
          speaker: 'clara',
          speech: 'Week cells use the same green to red scale as the main grid. Zero is green. A quarter is yellow green. A half is yellow. Three quarters is orange. One is red. Empty counts as zero.'
        },
        {
          speaker: 'liam',
          speech: 'The Legend tab has that colour strip plus the department, discipline, and role codes from the employee master. Discipline and trade are the same field. Open Legend when a code looks unfamiliar, then go back to the chart.'
        }
      ]
    }
  ],

  'indirect-resources-help': [
    {
      title: 'Who is working where?',
      icon: '👥',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every planning meeting eventually hits the same question: who do we actually have available, and when? This dashboard answers it for indirect resources.'
        },
        {
          speaker: 'liam',
          speech: 'It is a simple grid: one row per person, one column per week, and each cell holds that person\'s allocation for that week. String the weeks together and you see the staffing picture for the year — who is buried, who is free, and where the gaps are coming.'
        }
      ]
    },
    {
      title: 'Thinking in FTEs',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'The week cells speak in F T E, which stands for full time equivalent. A one means fully committed that week, a half means fifty percent, a quarter means twenty five percent, and an empty cell means nothing at all.'
        },
        {
          speaker: 'liam',
          speech: 'You can type any value, but most planning happens in those clean quarter steps. F T E lets you add up a person\'s commitments across several jobs and instantly see whether the total makes sense.'
        }
      ]
    },
    {
      title: 'The over-allocation guardrail',
      icon: '⚠️',
      turns: [
        {
          speaker: 'clara',
          speech: 'When you add a new assignment, the system checks that person\'s total across every job for the overlapping dates. If it would push past one hundred percent, a warning shows their existing assignments and the new total.'
        },
        {
          speaker: 'liam',
          speech: 'You can override if the double booking is intentional, or cancel and adjust. Pro tip: record vacations with the job number set to Holiday so time off counts toward the person\'s total just like project work.'
        }
      ]
    },
    {
      title: 'Import that refuses bad rows',
      icon: '📥',
      turns: [
        {
          speaker: 'clara',
          speech: 'Excel import and export are still here for bulk loading, but import is picky on purpose. Every employee code and name, job number and name, and estimate code and name must match the live databases that feed this dashboard — Admin Employees, Projects, and Estimates.'
        },
        {
          speaker: 'liam',
          speech: 'If a row does not match, it is not imported. The person who ran the import gets an email listing why each row failed and how to fix it — correct the file, or fix the master data first. Holiday and Not Awarded are allowed without a project match. Employee cleanup may rename people over time, so always use the live Admin list.'
        }
      ]
    },
    {
      title: 'Emails that keep the plan honest',
      icon: '📧',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every other Monday at six in the morning the Resource Plan email goes out. One short note, one workbook: the field plan, who is over allocated, who is under allocated, open needs, and accounting cleanup. People marked Terminated or On Leave are left out of the allocation tabs.'
        },
        {
          speaker: 'liam',
          speech: 'The workbook only includes live work: Holiday rows, completed status, and jobs or estimates archived in Admin are left off. Replies go to Project Controls. When an estimate is awarded, those Not Awarded plan rows pick up the new job number automatically. And every day, Yousef and Behzad get a data check email comparing the dashboard to those same source databases. Turn Resource Plan off under Profile, Notification Settings.'
        }
      ]
    },
    {
      title: 'Fast entry and zooming out',
      icon: '⚡',
      turns: [
        {
          speaker: 'clara',
          speech: 'Day to day entry still feels like a spreadsheet: click to edit, drag fill across weeks, and start plus finish plus allocation pre-fills a whole range. Control Z undoes a bad drag.'
        },
        {
          speaker: 'liam',
          speech: 'For the meeting view, the grid already sorts current week first — people allocated this Monday, or whatever From date you picked, sit at the top. Untick that box or click a column header if you want a different order. Open Analytics for the histogram, the pivot table — same week colours as the grid — and over or under allocation. The window stays one size when you switch tabs. Down the road this may become hour based from cost breakdowns — until then, keep the cells honest and the staffing picture stays honest too.'
        }
      ]
    },
    {
      title: 'Jump back from the audit log',
      icon: '📜',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every change on this plan is written to the audit log. Click a row there and the grid jumps to that person and the exact week or field that changed.'
        },
        {
          speaker: 'liam',
          speech: 'Superadmins also get a revert button, same idea as the progress tracker. It puts the previous value back and records that you did it, so you can always see who undid what.'
        }
      ]
    }
  ],

  'material-management-help': [
    {
      title: 'From takeoff to job site',
      icon: '📦',
      speech: 'Between the moment someone counts up the material on a drawing and the moment it arrives on site, there is a chain of custody that is surprisingly easy to fumble. This dashboard is that chain. Whenever a technical coordinator sends a material takeoff to purchasing, every requisition line lands here automatically, with its requested quantity already including waste. From this one screen, purchasing records what is in stock, raises purchase orders for the rest, and tracks receipts until everything is in hand.'
    },
    {
      title: 'Only order what you need',
      icon: '🧮',
      speech: 'Here is the clever part, and it is the heart of the whole page. For each line, you enter what is already on hand in the in stock field, and the remaining to P O quantity recalculates itself instantly: requested, minus in stock, minus what is already on order. That one running number means you only ever purchase the true remainder. No more ordering four hundred metres of pipe when a hundred and fifty are already sitting in the yard, and no more discovering the shortfall three weeks before installation.'
    },
    {
      title: 'Tracking the order home',
      icon: '🚚',
      speech: 'Once a purchase order goes out, the line keeps the whole story in one place: the P O number, the quantity ordered, and the quantity received so far, with a status you update as things progress. The K P I cards across the top keep score for you, showing how many requisitions are in play, how many lines are outstanding, and how many are fully received. When those outstanding numbers head toward zero, you can feel the project relaxing.'
    },
    {
      title: 'Email the supplier directly',
      icon: '✉️',
      speech: 'One feature that saves real time: tick the checkboxes on the lines you want to order, click email supplier, and the order list goes out as an Excel file straight from the portal. No downloading, no attaching, no forwarding from your inbox. It is a small thing that removes a whole manual step, and it keeps the order tied to the lines it came from.'
    },
    {
      title: 'One page, many doors',
      icon: '🚪',
      speech: 'Last thing worth knowing: this same dashboard shows up in more than one place. Under procurement you get the master view across all estimates and jobs. Open it from estimation or from project management, and it arrives pre filtered to that one estimate or job. Same data, different lens, so purchasing sees the whole board while a project team sees just their pieces. That is material management: record the stock, order the remainder, and track it home.'
    }
  ],

  'safety-training-help': [
    {
      title: 'Never blindsided by an expiry',
      icon: '🦺',
      speech: 'There are few worse surprises on a job site than discovering a certification expired last month. This page is built so that never happens. It is the safety training database: every training record, certification, and expiry for every employee, tracked automatically and flagged a full two months before anything lapses. Instead of chasing spreadsheets and sticky notes, safety gets one living source of truth that watches the calendar for you.'
    },
    {
      title: 'Four statuses tell the story',
      icon: '🚦',
      speech: 'Every record wears one of four statuses. Current means the certificate is valid, with more than sixty days of runway or no expiry at all. Due soon means it expires within sixty days, the same two month early warning the welder continuity log uses. Expired means the date has passed and it is time to book a refresher. And scheduled means the training date is in the future and nothing has been completed yet. The rows even tint themselves so expired and due soon records stand out, and on the dashboard you can click the expired or due in thirty days cards to jump straight to those records with the filter already applied.'
    },
    {
      title: 'The digest does the chasing',
      icon: '📧',
      speech: 'Here is the part that quietly does the most work: on the first Monday of every month at seven in the morning, everyone in the safety department automatically receives an email digest listing every expired and due soon certificate. Nobody has to remember to run a report. And when a line does not belong in that digest, maybe an employee who has left, or a course that no longer applies, the little bell on that record mutes its notifications, and muted lines never appear in the monthly email. It is a small toggle that keeps the digest signal clean.'
    },
    {
      title: 'Taming a big grid',
      icon: '🔽',
      speech: 'With hundreds of records, the viewing tools matter. By default the grid shows only the latest certificate per employee and course, which keeps things tidy, and unticking latest cert only reveals the full history when you need to dig. Tick expired only to focus on lapsed certificates. Group by employee collapses everything into one row per person with expired and due soon badges, like collapsible groups in Excel. Funnel filters and sorting live in every column header, the columns button lets you choose which columns you see and remembers your choice, and checkboxes enable batch actions like dismissing notifications or deleting in bulk.'
    },
    {
      title: 'Proof, cost, and permissions',
      icon: '🔐',
      speech: 'Click any training row to open the full record: edit the fields, view certificates right in the page for P D Fs and images, download or remove attachments, or delete the line. When you add a person, start typing their name or employee code and the admin panel list fills in the match. Open Course Library and hit add course to grow the catalog; every column has the same funnel filters and sorting as the records grid. Set a cost on each course, and new records plus C S V imports stamp that cost onto the training line without rewriting older history. The dashboard then shows total cost, a pie by category, and the top courses by spend. Exports to P D F, Excel, or email always capture exactly what you are looking at. Safety edits; project managers, superintendents, general foremen, foremen, and management can view; every change lands in the audit log. That is the training database. Let it watch the expiry dates, so your people are always cleared to work.'
    }
  ],

  'estimate-management-help': [
    {
      title: 'Where every project is born',
      icon: '🌱',
      speech: 'Every project in this portal starts its life on this page, as an estimate. Before there is a job number, before there is a schedule, there is a bid being prepared, and this is where it gets registered and tracked. The page follows a simple, satisfying arc: create the estimate, work the bid, win the contract, and watch a project appear. Four steps, and the handoff between estimating and project execution happens without anyone retyping a thing.'
    },
    {
      title: 'Creating and working the bid',
      icon: '📝',
      speech: 'Step one is creating the estimate: give it an estimate number and a name, then pick the division and the disciplines that apply. That estimate number matters more than it looks, because it is what drives the estimating department\u2019s work across the portal. Then comes step two, the long middle of the story, where the estimating team prepares the bid. Through all of that, the estimate simply sits in not awarded status. There is nothing to update or babysit while the pricing comes together.'
    },
    {
      title: 'The award moment',
      icon: '🏆',
      speech: 'Then the call comes in: you won. Here is where the magic happens. Click award, enter the job number, and the estimate flips to awarded status. In that same moment, the portal automatically creates a full project carrying over the same name, division, and disciplines from the estimate. One estimate can win more than one job. Award it again for a lump sum job and a time and material job, and each click creates another project. The first job number stays on the estimate, and both are listed. No duplicate data entry, no setup checklist, no chance for the project record to drift from what was actually bid.'
    },
    {
      title: 'Ready for kickoff',
      icon: '🚀',
      speech: 'And that is really it. The new project appears in the projects tab, ready for progress tracking, and all admin and superadmin users are assigned to it automatically, so the right people have access from minute one. From estimate number to live project in a couple of clicks. Win the work, click award, and let the portal handle the paperwork.'
    },
    {
      title: 'When the wrong bid was awarded',
      icon: '🩹',
      turns: [
        {
          speaker: 'clara',
          speech: 'The list has Manage, Award, Archive, Delete. There is no Edit. Manage is for the name, the client, and who can see the bid. Award is the only way a job is born, and it now reads the estimate name and client back to you before it commits, because neighbour codes on the same client look alike.',
        },
        {
          speaker: 'liam',
          speech: 'If the job number is right and the bid was the neighbour, a superadmin opens that job under Project Management and moves the award onto the right estimate. People already assigned to the job stay put. Changing the job number itself is only allowed while the job is still empty. Once tracker or time data exists, that clean-up stays a database job.',
        },
      ],
    },
  ],

  'hiring-layoff-help': [
    {
      title: 'One master list',
      icon: '👥',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome to Hiring and Layoff, the single master employee list for the portal. Think of it as the roster that feeds Time Tracker, Indirect Resources, the welder log, and portal users. There is not a second competing employee database anymore.'
        },
        {
          speaker: 'liam',
          speech: 'Here is the hiring flow. Tanya adds the person in Maestro, then adds their four digit employee code and name here with status Active. Then the ball moves to Jordan, who creates their company email in Entra.'
        },
      ],
    },
    {
      title: 'From Entra to Time Tracker',
      icon: '🔄',
      turns: [
        {
          speaker: 'clara',
          speech: 'Once Entra has the email, the portal sync picks it up, writes it onto this master list, and creates a portal user. That person can open Time Tracker right away to enter their own hours. Ball in court columns tell you who is waiting on Entra, sync, or a role assignment.'
        },
        {
          speaker: 'liam',
          speech: 'When someone leaves, Tanya changes status to On Leave or Terminated. The portal account deactivates with them. Use the short department, discipline, and role codes from the help legend, and tick Indirect only for people who belong on the Indirect Resources plan.'
        },
      ],
    },
  ],

  'welder-continuity-help': [
    {
      title: 'Welders come from the master list',
      icon: '🔥',
      turns: [
        {
          speaker: 'clara',
          speech: 'The welder continuity log tracks every welder, their welder number, their process, and when their certificate runs out. The big idea here is that welders are never typed in by hand. You pick them from a live dropdown of active employees on the master list.'
        },
        {
          speaker: 'liam',
          speech: 'Right, and that dropdown does more than save keystrokes. It guarantees every entry carries a real four digit employee code, so the welder connects cleanly to Time Tracker, safety training, and everything else the master list feeds.'
        },
      ],
    },
    {
      title: 'Automatic sync and expiry warnings',
      icon: '📡',
      turns: [
        {
          speaker: 'clara',
          speech: 'And here is the part people miss: the moment you save an entry, the welder number is written back onto that person\u2019s row on the master employee list, automatically. No separate step, no drift between the two.'
        },
        {
          speaker: 'liam',
          speech: 'Keep an eye on the Need Certificate tab too. It flags anyone with no welding activity in the last six months, giving you a two month runway before a certificate quietly expires. And if a welder is laid off, they leave the dropdown but their whole history stays on the log.'
        },
      ],
    },
  ],

  'coding-structure-help': [
    {
      title: 'The language of project costs',
      icon: '🧭',
      speech: 'Every dollar on every project ends up in the accounting system tagged with a code, and if you have ever stared at one of those codes wondering what it means, this page is your decoder ring. The coding structure is the shared language between the field, the estimators, and accounting. Learn its logic once, and suddenly a wall of numbers in a cost report starts reading like plain English.'
    },
    {
      title: 'Five digits, one system',
      icon: '🔢',
      speech: 'Every cost in Maestro carries a five digit operation code, and the first two digits tell you the category. Codes starting with zero zero are contract expenses, things like material, subcontracts, and rentals. Zero one is indirect labour, the support and supervisory staff. Zero two is general direct labour. Then the twenty one through twenty nine thousand ranges hold direct labour for each discipline, one range per discipline. Codes in the nineties are revenues, the billings going out to clients. Two digits in, and you already know what kind of cost you are looking at.'
    },
    {
      title: 'The plus sixty thousand trick',
      icon: '➕',
      speech: 'Here is the pattern that people find genuinely satisfying once it clicks. There are nine disciplines, each with a three letter acronym, and each with its own direct labour code range. When a change order or extra comes along, its code is simply the discipline\u2019s labour code plus sixty thousand, which is why the extras live in the eighty one through eighty nine thousand ranges, mirroring the disciplines exactly. So if you know a discipline\u2019s labour code, you already know its extras code. One rule, and an entire block of the coding structure unlocks.'
    },
    {
      title: 'Hierarchy and cost types',
      icon: '🏗️',
      speech: 'The codes also stack into a hierarchy: sections at the top, though those are rare, then phases, which are the group headers written in all capital letters, like piping direct labour, and finally activities, which is where transactions actually post, things like project manager or material. Riding along with the codes are cost type letters, one letter per kind of cost, covering labour, material, subcontracts, equipment, and so on. One quirk to remember: the letter S pulls triple duty, shared by subcontracts, intercompany transactions with the United States, and regulatory inspection fees.'
    },
    {
      title: 'Projects and departments',
      icon: '🗂️',
      speech: 'At the project level, a multi discipline job has a master project number with subcontracts hanging off it per discipline, each identified by the job number plus the discipline acronym, and each using its own discipline\u2019s code range. Time and materials jobs keep it minimal, with just nine activity codes and no discipline ranges at all. Department codes describe the customer: a letter for Canada or the United States, a two letter province or state, then the customer name, so a code reading C O N B A S F unpacks to Canada, Ontario, B A S F.'
    },
    {
      title: 'Templates and where this leads',
      icon: '🔗',
      speech: 'When a new project needs its coding, you pick from ready made templates instead of building from scratch: large multi discipline with the full structure, small multi discipline with fewer support roles, and focused templates for pipe fabrication and the tank shop. And this structure is not just for accounting. Forecasting maps its Maestro imports to these operation codes, change management tracks extras in those eighty thousand ranges, and cost breakdowns align to the same cost type letters. Master this one page, and half the portal makes more sense. Happy decoding.'
    }
  ],

  'master-component-list': [
    {
      title: 'One table to rule the scope',
      icon: '🧩',
      speech: 'Think about how project knowledge usually lives: the schedule sits in Primavera, the piping take offs sit in one spreadsheet, the structural details in another, the crane hours in a third, and the only thing connecting them is somebody\u2019s memory. The master component list exists to end that. It is one giant, clean table for the whole project scope, where every activity, every spool, every steel tag, every module and every hour can sit side by side. The goal is simple and a little ambitious: be on top of every single detail of the project, in one place.'
    },
    {
      title: 'Columns that grow with the project',
      icon: '📊',
      speech: 'Most tables in the portal have a fixed set of columns. This one deliberately does not. Columns here are fluid: they are created automatically whenever you import a file, and you can add, rename, reorder, hide or delete them yourself at any time. Need an activity I D from the May schedule next to the activity I D from the July schedule, next to a tag, a drawing number and a supplier? That is exactly the point. There is no practical limit, so a hundred columns is perfectly normal here.'
    },
    {
      title: 'Importing like Power Query',
      icon: '📥',
      speech: 'Here is the clever part. The import button accepts two kinds of files: Primavera X E R schedules, and Excel sheets like material take offs, detail estimates or module lists. Either way, you get two modes. Append simply adds every row as a new row, which is what you want the first time a source comes in. Merge is the interesting one: you pick a match key, say tag, S O V code, or activity I D, and the import fills its columns into the rows that already exist, exactly like a lookup or a Power Query merge. Rows that do not find a match can be appended as new rows, so no information is ever silently dropped.'
    },
    {
      title: 'The sources column never lies',
      icon: '🗂️',
      speech: 'Every row carries a small set of colored chips called sources. They record exactly which files contributed data to that row: a schedule snapshot, a structural detail sheet, a piping take off, or a manual edit. When a single row has been touched by three different imports and a hand correction, you can see all four at a glance, and the imports tab keeps the full log of who loaded what, when, and how many rows it changed. When a number looks suspicious, the sources tell you where to go check.'
    },
    {
      title: 'Working the grid',
      icon: '✏️',
      speech: 'The grid works the way you would hope. The search box scans every column at once. Each column header has a funnel for filtering by value and click sorting in both directions. Click any cell to edit it in place; your name and the time are stamped automatically, and the row picks up a manual source chip. With a wide table, pin your key columns, like tag or activity I D, using the little pin icon, and they stay frozen on the left while everything else scrolls. And once you have a filter, sort and column setup you like, save it as a named view. Views can stay personal or be shared with the whole team, so everyone can open the exact same slice of the table with one click.'
    },
    {
      title: 'More than one master',
      icon: '🗃️',
      speech: 'Some projects need more than one of these tables, maybe one per phase, one for a what if scenario, or one for a completely different scope. The dropdown next to the title lets you create several masters and hop between them. Each master is fully independent: its own columns, its own rows, its own imports and its own saved views. Nothing bleeds between them, so you can experiment freely in one without touching the real one.'
    },
    {
      title: 'Health checks and computed columns',
      icon: '🩺',
      speech: 'Two power tools round it out. The health tab plays doctor on your data: it finds duplicate values in your key column, rows that a merge never matched, rows where the key is blank, and the emptiest columns, and one click jumps you to the offending rows in the grid. Computed columns do live math between two columns: the hour difference between two schedule snapshots, the schedule slip in days between two finish dates, or a simple ratio. They calculate on the fly on every row, and the analytics tab charts the biggest movers so the largest slips rise straight to the top.'
    },
    {
      title: 'A rhythm that keeps you on top',
      icon: '🔁',
      speech: 'A practical routine: when a new schedule lands, import the X E R with a clear label like X E R July nineteen, merging on the activity I D so the new dates sit beside the old ones. When an estimate sheet is frozen, bring in its rows and merge on tag or S O V. Then run the health tab to catch duplicates and unmatched rows while the import is fresh, and use the filters to ask real questions, like which components on the critical batch still have no drawing number. Keep the labels honest and the merges keyed, and this table quietly becomes the single most complete picture of the project anywhere. Happy tracking.'
    }
  ],

  'evm-report-help': [
    {
      title: 'Resource Control in one place',
      icon: '📈',
      turns: [
        {
          speaker: 'clara',
          speech: 'E V M Report is your Resource Control page. Spent comes from Maestro. Earned comes from P six, and you pick how — four ways under Settings, we will break them down in a second. The blue budget curve is always the baseline plan from the proposal — extras never draw budget bars. The K P Is budget whatever scope you filter: all means baseline plus extras priced from the Current schedule, so percent complete stays honest when extras earn hours. Cum remaining continues on from the earned curve.'
        },
        {
          speaker: 'liam',
          speech: 'Tabs are Histogram, Staffing, K P Is, and Progress. Filters stay under every tab so you never leave the page. The filter bar is labeled dropdowns: Budget, Current, Curve for which lines you plot, and Activities as the P six W B S tree — check a band to take that whole branch onto the histogram. On Curve, tick Bars, Lines, or both. Tick E V M marks and the histogram turns into the classic S curve picture: a data date line, the budget at completion line across the top, the estimate at completion line, and brackets for the gaps. S V is earned against planned, C V is earned against spent, E T C is the hours still to spend, and V A C is budget against the forecast at the end. Hover a week for S P I and C P I. Discipline is not a portal list — the checkboxes are the live P six Discipline field on the selected X E R, an activity code or a U D F with that name. Activities without that assignment show as no value. Settings sits on the top button row with Help, and Budget Hours Baseline lives in there. Open Help for the audio guide and the full K P I pocket guide for new project managers.'
        }
      ]
    },
    {
      title: 'Four ways to count Earned',
      icon: '⚖️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Earned source lives under Filters, then Settings. As-is shows the P six Resource Assignments cells. Group by week or month and you are still looking at those same daily cells — a month is the calendar month in P six, not the Sunday weeks added together. When someone runs summarize in P six, the portal reads P six\u2019s own stored spread for earned and for remaining — an exact match to the P six screen. There is no background service for summarize in P six Professional, so it is a manual step: once a week, about thirty seconds, right after the weekly update — and again after any reschedule. Miss it and nothing breaks: the portal compares the stored spread against the live units. If the totals differ and someone edited the project after the last summarize, the stored cells are stale, so the portal rebuilds them from the live units and the planned rate and calendar until the next summarize. The header line tells you which one you are looking at — exact, or rebuilt — and it also shows the time the portal last read P six. Press Refresh and that time moves to now. One more thing: the data date is P six\u2019s own data date for the copy you picked, the same one the Projects layout shows, not the date written in the file name.'
        },
        {
          speaker: 'liam',
          speech: 'Temp is the weekly delta: this week\u2019s dated P six actual units total minus the previous weekly upload, and Current is the last point after the newest dated file. Perm reads P six store period performance history instead — great once accounting stores periods every week, stale if they skip. Manual is the leveled Power B I dump from the Goodyear presentations, job seventy five nine nine eight. Other jobs stay on As-is. If a week looks off, flip the modes and see which one your schedule actually supports.'
        }
      ]
    },
    {
      title: 'KPIs in plain English',
      icon: '📘',
      turns: [
        {
          speaker: 'clara',
          speech: 'B A C is the contract hours: baseline plus approved extras. Extra is not a P six flag. The resource I D needs an E X token of its own, like estimate key dash P I dash E X. Or the name says Extra, or Additional Scope. Filter baseline, extra, or both, and B A C follows that filter. P V is what you planned to have earned by today. E V is what you actually earned. A C is what you spent.'
        },
        {
          speaker: 'liam',
          speech: 'F A C and E T C use a formula you pick in Filters, next to Assume this week equals last week. The default is typical C P I — budget divided by cost performance. You can switch to atypical, C P I times S P I, schedule only, P six remaining, or earned plus remaining. V A C is underrun or overrun versus the budget, and T C P I is how hard leftover work must work to still hit the budget. Open Figures when you want the thirteen cards.'
        }
      ]
    },
    {
      title: 'KPI family is the old Power B I board',
      icon: '🗂️',
      turns: [
        {
          speaker: 'clara',
          speech: 'K P I family is leftover from the Power B I Resource Control page. It only slices the K P I cards — B A C, E V, A C, S P I, C P I — into the six families that presentation used: Overall, Mech, Pipe, Elec, Indirect, and Pipe Fab. It is not the histogram trade list.'
        },
        {
          speaker: 'liam',
          speech: 'H VAC, civil, and structural never had their own family on that board, so they only show under Overall. Leave every family ticked unless you are matching an old Power B I slide. Spent from Maestro uses the same six buckets from the labour codes: E L E, P I P, M W R, P F S, and I N. Happy planning.'
        }
      ]
    },
    {
      title: 'Fab, Field, Extra, and how to name a resource',
      icon: '🏷️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Fab versus Field is not a P six column. If the resource name contains Fab — Fabrication — we call it fab. Everything else is field. Put Fabrication in the name when it is shop work, even if the I D already ends with P F. A P F I D with no Fab in the name is field.'
        },
        {
          speaker: 'liam',
          speech: 'From now on name resources like this. The I D is estimate key, dash, trade token, then optional dash F A B, then optional dash E X. The name is the trade words, plus Fabrication when it is shop, plus Extra or Additional Scope when it is extra. Tokens: P I piping, P F pipe fab, E I electrical, M W mechanical, S T structural, H V H VAC, D U ducts, C V civil, B M boilermaker, T K tank, I N indirect. The Discipline U D F should use the same words as the name. The old suffix map is only the fallback when that U D F is missing.'
        }
      ]
    },
    {
      title: 'Saturdays become the next Sunday',
      icon: '📅',
      turns: [
        {
          speaker: 'clara',
          speech: 'P six weeks cut off at Sunday midnight — zero zero hours. The histogram uses those Sunday buckets directly. Maestro spent is week ending Saturday, so Saturday hours sit on the next Sunday. An X E R named June twenty-first is that remaining week. Earned, planned, and spent sit on the Sunday before — June fourteenth — so those two weeks never plot as the same update.'
        },
        {
          speaker: 'liam',
          speech: 'Man-weeks is the default. Period hours are divided by the hours per week on the calendar from the selected Current or Budget P six schedule. What-if ranges override that calendar for a date span — hours stay the same, the crew changes. The left axis shows whole man-weeks, no decimals. On an estimate you will usually only see the budget curve until the job is awarded.'
        }
      ]
    },
    {
      title: 'Legend by — one dropdown',
      icon: '🎨',
      turns: [
        {
          speaker: 'clara',
          speech: 'The Legend by dropdown colours Budget, Remaining, and Earned, and Spent when the hours can be matched. It opens on resource I D. You can switch to one colour, discipline, any P six column, an Accounting column such as op code or cost type, or a Time Tracker column that already works as a slicer. Earned uses the same P six columns as Remaining. Spent follows the operation code through the Accounting coding structure, or the Time Tracker column itself. A column a curve does not carry stays one colour. Switching the legend never changes a bar\u2019s height, only how it is split. The week total always sits on top of the bar — stacked or not — so you can read the crew without adding colours in your head. Tick Counts on bars next to Man hours or Man weeks to print each colour\u2019s count inside the stack as well. Same toggle writes men on the Staffing crew bars.'
        },
        {
          speaker: 'liam',
          speech: 'Click a name in the legend to spotlight that series. Everyone else fades; click again and they come back. The series does not disappear. Click the coloured square to pick a new colour for that series — same as the Corporation Resource Histogram. To take a trade off the chart altogether, use the Resources filter. Happy planning.'
        }
      ]
    },
    {
      title: 'More than one Current file',
      icon: '📚',
      turns: [
        {
          speaker: 'clara',
          speech: 'Some estimates keep the job in more than one P six project — A, B, and C current files, or a dated weekly per area. The Current picker lists every file for this estimate, even the empty shells, so you can see what is actually in P six.'
        },
        {
          speaker: 'liam',
          speech: 'Auto still picks the latest weekly that has activities. Check two or more boxes and the histogram adds their hours together. Do not check two copies of the same hours or you will double count. This is live P six, not a Monday sync wait. Happy planning.'
        }
      ]
    },
    {
      title: 'Programs and what-if scenarios',
      icon: '🎛️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Big estimates can be a program: package A, package B, package C, each scheduled on more than one crew calendar — four tens, five tens, six tens. Name the P six file estimate dash package letter dash calendar, like dash A dash four X ten, or just end the project name with A four x ten. The picker groups them by package and shows the calendar instead of a date.'
        },
        {
          speaker: 'liam',
          speech: 'Then use the scenario chips at the top of the picker. Click four x ten and the portal checks that calendar for every package — the whole program lands in one histogram. Pick one calendar per package: if a package is checked twice the picker warns you, because those hours would double count. With no weekly update in P six, Auto builds the base program by itself.'
        },
        {
          speaker: 'clara',
          speech: 'And here is the part that matters in a presentation: the weekly hours are the same on every calendar, but the crew is not. Four x ten crews work forty hours a week, five x ten crews fifty, six x ten crews sixty. Man weeks and the staffing plan divide by the real P six calendar on each file — the day patterns, not the file name — so six x ten shows fewer people, not the same chart three times. If a file carries no usable calendar, the scenario name fills in, and the calendar slicer tells you which one is driving.'
        }
      ]
    },
    {
      title: 'What-if calendars for one trade',
      icon: '🗓️',
      turns: [
        {
          speaker: 'clara',
          speech: 'What-if calendars answer a one-trade question. The project manager asks, what if electrical works four tens for the rest of the job while everyone else stays on five tens. Open What-if, pick Electrical, add a range from remaining start to finish, and set forty hours a week. Hours on the histogram stay the same. The crew changes, because men equal hours divided by that week. Electrical Extra inherits Electrical unless you set it separately. The rows follow this job’s Discipline values, not an old ten-item list.'
        },
        {
          speaker: 'liam',
          speech: 'Do not use what-if for a whole program calendar change. That is a new P six file named four X ten. What-if is the lighter tool when only one trade changes hours per week. Keep it. Click a legend square any time to change a colour — hash colours are only the default. Happy planning.'
        }
      ]
    },
    {
      title: 'Staffing is the hireable curve',
      icon: '👷',
      turns: [
        {
          speaker: 'clara',
          speech: 'The Staffing tab is the old Excel staff plan on live P six extracts: baseline hours and manpower from the schedule you pick, then the Current schedule as earned units to the data date and remaining units after it, divided by each resource’s own P six calendar.'
        },
        {
          speaker: 'liam',
          speech: 'Hiring and layoff is this week minus last week, and you can double click a future cell to type your own number — the forecast crew follows your plan, like the Excel did. Difference is current minus baseline. How it works lives in Help. Chart controls and the live data date live under Filters, Staffing plan. Safety and Supervision plans are the next cards over. Happy hiring.'
        }
      ]
    }
  ],

  'staffing-plan-help': [
    {
      title: 'What lives where',
      icon: '📍',
      turns: [
        {
          speaker: 'clara',
          speech: 'The Staffing tab itself is the chart and the six tables. Peak men, data date, and chart controls live under Filters, Staffing plan: which baseline, smooth forecast, past or future or all, and curves versus crew stack. How the numbers are built, the colour key, and how hiring works live here in Help, and in Listen.'
        },
        {
          speaker: 'liam',
          speech: 'That same Filters card also shows what you are looking at right now: the data date, the F A C formula and factor, and which schedule is the baseline. Open Help from there any time. Double click a future hiring cell on the tab when you want to type your own plan.'
        }
      ]
    },
    {
      title: 'Six tables, straight from P six',
      icon: '📈',
      turns: [
        {
          speaker: 'clara',
          speech: 'Everything on this tab is a P six extract, one row per resource i d. Baseline hours are the baseline schedule resource assignments. Baseline manpower is those hours divided by the P six calendars. Then the Current schedule: earned and remaining hours in one table — actual units up to the data date, remaining units after it — and the same thing as manpower.'
        },
        {
          speaker: 'liam',
          speech: 'Hiring and layoff is this week’s manpower minus last week’s. Difference is earned plus remaining minus baseline. At the start of a job, baseline and current are naturally the same numbers — the drift you see later is reschedules, earnings, and extras. That drift is exactly what the difference table measures.'
        }
      ]
    },
    {
      title: 'Baseline is a schedule you pick',
      icon: '📐',
      turns: [
        {
          speaker: 'clara',
          speech: 'Baseline defaults to the Current schedule, the plan of record, so it moves with every weekly update. Switch it to the budget or proposal schedule when you want to compare against the bid. The exact schedule instance comes from the Budget and Current pickers in Filters.'
        },
        {
          speaker: 'liam',
          speech: 'And the calendars are real: every resource divides by its own P six calendar week hours — forty, fifty, sixty — not one global number. Hover a resource name to see its calendar. A what-if range in Filters overrides the calendar for a date span if you want to test a schedule compression.'
        }
      ]
    },
    {
      title: 'Hiring and layoff drives the forecast',
      icon: '✍️',
      turns: [
        {
          speaker: 'clara',
          speech: 'This is the Excel behaviour people asked for. Double click a future hiring cell and type your own number. The forecast manpower rebuilds from your entries, week by week: next week’s crew equals this week’s crew plus your hiring or layoff. Past weeks are history and stay locked.'
        },
        {
          speaker: 'liam',
          speech: 'Overridden cells turn amber, and the strip chart, the next four weeks card, the manpower table, and the difference table all follow your plan. Leave a cell empty to fall back to the calculated value, or hit reset to clear every override. Overrides are saved per job in this browser. The hours tables never move — they stay pure P six.'
        }
      ]
    },
    {
      title: 'The F A C formula and smoothing',
      icon: '⚙️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Remaining listens to the F A C formula you picked in Filters — typical budget over C P I, atypical, schedule, or pure P six remaining. The factor chip shows what is applied; factor one means untouched P six.'
        },
        {
          speaker: 'liam',
          speech: 'Smooth forecast is optional and starts at zero, which means the exact P six extract. Slide it and each remaining week averages with up to five weeks either side, Excel style — edges shrink, zeros stay, and the window never reaches back across the data date.'
        }
      ]
    },
    {
      title: 'Reading the curves',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'Blue is actual, the earned crew up to the data date. Green is remaining, the forecast crew after it. They meet on the yellow data-date week — same headcount — then green carries the forecast. A drop there is a real layoff, shown on the red bar underneath. The baseline runs gray up to the data date and black after.'
        },
        {
          speaker: 'liam',
          speech: 'The legend sits on the left of the curve and stays frozen when you scroll, along with the men axis and the resource names. Crew stack is the second view — stacked bars per resource or per discipline with the baseline dashed on top. Tick Counts on bars to print men inside each block, and the same toggle labels stacked histogram bars. Click a legend square to change a colour. The timescale is three layers like P six: year, month, week starting Sunday.'
        }
      ]
    },
    {
      title: 'Filters and the twins',
      icon: '🎛️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every filter on the pane applies — discipline, resources, shop or field, baseline versus extra, date range, even a clicked activity. Past, future, or all clips the window; future is the default because a manning meeting cares about next month.'
        },
        {
          speaker: 'liam',
          speech: 'If two rows show the same trade, they are two P six i ds — the baseline crew and the extra with E X in the name. Rows that are all zeros in the window are hidden. Download Excel or P D F from the Staffing tab — Excel has every week, P D F is the letterhead summary plus a landscape date table. Safety, Supervision, and Indirect extras have the same two buttons. Happy hiring.'
        }
      ]
    },
    {
      title: 'Supervision is the same idea as safety',
      icon: '👷',
      turns: [
        {
          speaker: 'clara',
          speech: 'Settings, Safety has three rules. Keep the old one if you want — one safety for every N men, twenty five by default. Or switch to first then: no safety under twenty men, the first safety at twenty, then one more every forty. Or a third step: first safety at twenty, the next one forty men later, then one more every fifty. Open Safety plan from the histogram and it stays on that tab — the table counts the remaining bars you can see, not a cached Staffing curve. Supervision is the estimator’s cousin. Tick Plan and it still opens on Staffing. The key idea: supervision is per trade, per P six resource i d, never one ratio on the whole job. Each trade gets one foreman per eight of its own men, and every number is editable — you can even type it as a percent of crew.'
        },
        {
          speaker: 'liam',
          speech: 'General foremen follow a stricter rule. Under twenty four men in a trade, there is no G F at all. The first general foreman starts only when one trade reaches twenty four men — that is eight men per foreman times three foremen — and the next starts at forty eight. The all trades view is simply the week by week sum of what every trade needs on its own, because a G F is never shared across trades. Open Supervision from the histogram — it stays on that page. Downloads keep the vertical windows table and add a date-column sheet. Indirect extras is the next card: the G C and self-perform takeoff, quantity and duration from the twenty-month Excel, scaled to this job, empty rows hidden. Override any number — that is the estimate base. The amber strip under hiring shows the weekly counts. The base is always the direct crew — foremen and general foremen are the indirects, so they are computed from the direct men. Happy planning.'
        }
      ]
    }
  ],

  'budget-report-help': [
    {
      title: 'Where Budget Hours lives now',
      icon: '✊',
      turns: [
        {
          speaker: 'clara',
          speech: 'Budget Hours Baseline is no longer a separate menu item. Open E V M Report under Reports — that is your Resource Control page — then Settings on the top button row, and Budget Hours Baseline. It opens this same tool in a popup.'
        },
        {
          speaker: 'liam',
          speech: 'E V M keeps Spent, Earned, Proposal, Forecast, and the K P Is on one page. The baseline popup is for entering or reviewing shop and field budget hours by discipline, plus the live P six execution histogram.'
        }
      ]
    },
    {
      title: 'Estimation versus execution',
      icon: '📋',
      turns: [
        {
          speaker: 'clara',
          speech: 'Two gears. Estimation is the early S curve you type in by discipline, shop and field, before the job is fully scheduled. Execution flips to the live labour histogram from Primavera P six.'
        },
        {
          speaker: 'liam',
          speech: 'Those dated P six project names, like twenty five L G zero three one one dash a date, are weekly schedule updates in execution. The proposal baseline is the one marked with a P suffix, or proposal in the name — that is the budget plan once the job is awarded.'
        }
      ]
    },
    {
      title: 'Calendars and man-weeks',
      icon: '📅',
      turns: [
        {
          speaker: 'clara',
          speech: 'On E V M you can switch the Y axis between man-hours and man-weeks. The default calendar is read live from Primavera. Pick another P six calendar from the dropdown, or open What-if per discipline to force hours per week for scenario planning on remaining work.'
        },
        {
          speaker: 'liam',
          speech: 'On Manual for the Goodyear pilot, every trade starts at sixty, then the whole job moves to fifty in April. Only Electrical drops to forty in May. Piping and the rest stay at fifty. Other jobs are unchanged. Actual burned hours still come from Maestro for now; longer term Time Tracker becomes the source of truth. Happy planning.'
        }
      ]
    }
  ],
};
